// cleancode-ui/src/components/ReviewHistory.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from './Navigation';
import TrialStatus from './TrialStatus';
import { useEnvironment } from '../contexts/EnvironmentContext';
import './Login.css';

const ReviewHistory = ({ onLogout }) => {
  const navigate = useNavigate();
  const { apiBaseUrl } = useEnvironment();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/review-history`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch reviews');
      }

      const data = await response.json();
      setReviews(data.reviews || []);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      // Use mock data for now
      setReviews([
        {
          id: 1,
          type: 'github',
          title: 'AirbnbTutorial PR #2',
          url: 'https://github.com/Joffrmann37/AirbnbTutorial/pull/2',
          createdAt: '2024-01-15T10:30:00Z',
          status: 'completed',
          summary: 'Code review completed with 5 suggestions'
        },
        {
          id: 2,
          type: 'zip',
          title: 'Swift Project Upload',
          createdAt: '2024-01-14T15:45:00Z',
          status: 'completed',
          summary: 'Full project review with 12 recommendations'
        },
        {
          id: 3,
          type: 'inline',
          title: 'Inline Code Review',
          createdAt: '2024-01-13T09:20:00Z',
          status: 'completed',
          summary: 'Inline annotations generated'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const filteredReviews = reviews.filter(review => {
    if (filter === 'all') return true;
    return review.type === filter;
  });

  const downloadReview = async (reviewId, format = 'markdown') => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/review-history/${reviewId}/download?format=${format}`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to download review');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Set appropriate filename based on format
      const extension = format === 'pdf' ? 'pdf' : 'md';
      link.download = `review-${reviewId}.${extension}`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading review:', error);
      alert(`Failed to download review as ${format.toUpperCase()}`);
    }
  };

  if (loading) {
    return (
      <div>
        <Navigation onLogout={onLogout} />
        <div className="login-container">
          <div className="login-card">
            <div className="login-header">
              <h1 className="login-title">Review History</h1>
              <p className="login-subtitle">Loading your past reviews...</p>
            </div>
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div className="spinner"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navigation onLogout={onLogout} />
      <TrialStatus />
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <h1 className="login-title">Review History</h1>
            <p className="login-subtitle">Your past code reviews and insights</p>
          </div>

          <div className="dashboard-content">
            {/* Filter buttons */}
            <div className="filter-section" style={{ marginBottom: '2rem' }}>
              <div className="filter-buttons">
                {[
                  { key: 'all', label: 'All Reviews' },
                  { key: 'github', label: 'GitHub PRs' },
                  { key: 'zip', label: 'Project Uploads' },
                  { key: 'inline', label: 'Inline Reviews' }
                ].map(filterOption => (
                  <button
                    key={filterOption.key}
                    onClick={() => setFilter(filterOption.key)}
                    className={`filter-button ${filter === filterOption.key ? 'active' : ''}`}
                    style={{
                      padding: '0.5rem 1rem',
                      margin: '0 0.5rem 0.5rem 0',
                      borderRadius: '0.5rem',
                      border: '1px solid #ddd',
                      background: filter === filterOption.key ? '#4A90E2' : 'white',
                      color: filter === filterOption.key ? 'white' : '#333',
                      cursor: 'pointer'
                    }}
                  >
                    {filterOption.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Reviews list */}
            <div className="reviews-list">
              {reviews.length === 0 ? (
                <div className="no-reviews" style={{ 
                  textAlign: 'center', 
                  padding: '3rem 2rem',
                  background: 'white',
                  borderRadius: '0.5rem',
                  border: '1px solid #ddd'
                }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📝</div>
                  <h3 style={{ margin: '0 0 0.5rem 0', color: '#333' }}>No Reviews Yet</h3>
                  <p style={{ margin: '0 0 1rem 0', color: '#666' }}>
                    Start by running a code review to see your history here.
                  </p>
                  <button 
                    onClick={() => navigate('/review')}
                    className="login-button"
                    style={{ 
                      background: '#4A90E2', 
                      color: 'white',
                      padding: '0.75rem 1.5rem',
                      fontSize: '1rem'
                    }}
                  >
                    Start Your First Review
                  </button>
                </div>
              ) : filteredReviews.length === 0 ? (
                <div className="no-reviews" style={{ textAlign: 'center', padding: '2rem' }}>
                  <p>No reviews found for the selected filter.</p>
                </div>
              ) : (
                filteredReviews.map(review => (
                  <div key={review.id} className="review-card" style={{
                    border: '1px solid #ddd',
                    borderRadius: '0.5rem',
                    padding: '1rem',
                    marginBottom: '1rem',
                    background: 'white'
                  }}>
                    <div className="review-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                      <div>
                        <h3 style={{ margin: '0 0 0.25rem 0', fontSize: '1.1rem' }}>{review.title}</h3>
                        <p style={{ margin: '0', fontSize: '0.9rem', color: '#666' }}>
                          {new Date(review.createdAt).toLocaleDateString()} • {review.type.toUpperCase()}
                        </p>
                      </div>
                      <span className={`status-badge ${review.status}`} style={{
                        padding: '0.25rem 0.5rem',
                        borderRadius: '0.25rem',
                        fontSize: '0.8rem',
                        background: review.status === 'completed' ? '#d4edda' : '#f8d7da',
                        color: review.status === 'completed' ? '#155724' : '#721c24'
                      }}>
                        {review.status}
                      </span>
                    </div>
                    
                    <p style={{ margin: '0.5rem 0', fontSize: '0.9rem' }}>{review.summary}</p>
                    
                    {review.url && (
                      <p style={{ margin: '0.5rem 0', fontSize: '0.8rem' }}>
                        <a href={review.url} target="_blank" rel="noopener noreferrer" style={{ color: '#4A90E2' }}>
                          View on GitHub →
                        </a>
                      </p>
                    )}
                    
                    <div className="review-actions" style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
                      <button 
                        className="login-button"
                        style={{ 
                          background: '#28a745', 
                          fontSize: '0.9rem', 
                          padding: '0.5rem 1rem' 
                        }}
                        onClick={() => {
                          // Navigate to review page with this review's data
                          navigate(`/review?reviewId=${review.id}`);
                        }}
                      >
                        🔍 View Details
                      </button>
                      <button 
                        className="login-button"
                        style={{ 
                          background: '#6c757d', 
                          fontSize: '0.9rem', 
                          padding: '0.5rem 1rem' 
                        }}
                        onClick={() => downloadReview(review.id, 'markdown')}
                      >
                        📄 Markdown
                      </button>
                      <button 
                        className="login-button"
                        style={{ 
                          background: '#dc3545', 
                          fontSize: '0.9rem', 
                          padding: '0.5rem 1rem' 
                        }}
                        onClick={() => downloadReview(review.id, 'pdf')}
                      >
                        📄 PDF
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewHistory;
